import { useEffect, useState } from "react";
import * as React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Typography from "@mui/material/Typography";
import {
  AppButton,
  AppDialog,
  AppTable,
  AppTableCell,
  AppTableHeadCell,
  AppTableRow,
  FilterBar,
  FormSection,
  PageHeader,
  RowNumberCell,
  toast,
} from "../../components/ui";

import {
  ClioService,
  EntryType,
  MatterPreviewResponse,
  TaskPreviewResponse,
  ZoomCommunicationViewModel,
  ZoomService,
  ZoomTableRequest,
} from "../../api";
import "./ZoomPhone.scss";
import {
  Box,
  Checkbox,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  LeadsSelectAllHeadCell,
  mapLeadHeadCells,
} from "../Admin/LeadsManagement/LeadsTables/LeadTableHeader";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  DEFAULT_ROWS_PER_PAGE,
  HeadCell,
  TableInteractionsProps,
} from "../Admin/LeadsManagement/LeadsTables/LeadsTableConstants";
import { useDispatch, useSelector } from "react-redux";
import { startSpinner, stopSpinner } from "../../redux/actions/spinnerActions";
import { ClioMatterAutocomplete } from "../../components/shared/MatterAutocomplete";

dayjs.extend(utc);
dayjs.extend(timezone);

const PHONE_TABLE_HEAD: HeadCell[] = [
  { id: "path", disablePadding: false, label: "Type" },
  { id: "matterName", disablePadding: false, label: "Matter name" },
  { id: "clientPhone", disablePadding: false, label: "Callee phone" },
  { id: "clientName", disablePadding: false, label: "Callee name" },
  { id: "message", disablePadding: false, label: "Text message" },
  { id: "duration", disablePadding: false, label: "Duration" },
  { id: "created", disablePadding: false, label: "Created at" },
];

const MySwal = withReactContent(Swal);

const ZOOM_ORIGIN = "https://applications.zoom.us";
const ZOOM_IFRAME_SRC = `${ZOOM_ORIGIN}/integration/phone/embeddablephone/home`;

function sendZoomInitConfig() {
  const iframe = document.getElementById(
    "zoom-embeddable-phone-iframe"
  ) as HTMLIFrameElement | null;
  iframe?.contentWindow?.postMessage(
    {
      type: "zp-init-config",
      data: {
        enableSavingLog: false,
        enableAutoLog: false,
        enableContactSearching: false,
        enableContactMatching: false,
        notePageConfiguration: {},
        disableInactiveTabCallEvent: true,
      },
    },
    ZOOM_ORIGIN
  );
}

function reloadZoomIframe() {
  const iframe = document.getElementById(
    "zoom-embeddable-phone-iframe"
  ) as HTMLIFrameElement | null;
  if (iframe) {
    iframe.src = ZOOM_IFRAME_SRC;
  }
}

declare global {
  interface Window {
    onZoomPhoneIframeApiReady?: () => void;
  }
}

export interface PhoneContact {
  phoneNumber: string;
  name: string;
}

export default function ZoomPhone() {
  const user = useSelector((state: any) => state.user?.user);

  const [callMatterValue, setCallMatterValue] =
    React.useState<MatterPreviewResponse | null>(null);
  const [callContactOptions, setCallContactOptions] = React.useState<
    readonly PhoneContact[]
  >([]);
  const [callContactValue, setCallContactValue] =
    React.useState<PhoneContact | null>(null);
  const [callInProgress, setCallInProgress] = React.useState(false);

  const callContactValueRef = React.useRef(callContactValue);
  const callMatterValueRef = React.useRef(callMatterValue);

  const [matterValue, setMatterValue] =
    React.useState<MatterPreviewResponse | null>(null);
  const [taskValue, setTaskValue] = React.useState<TaskPreviewResponse | null>(
    null
  );
  const [taskOptions, setTaskOptions] = React.useState<
    readonly TaskPreviewResponse[]
  >([]);
  const [notesValue, setNotesValue] = React.useState<string | null>(null);

  const [zoomCommunications, setZoomCommunications] = React.useState<
    ZoomCommunicationViewModel[]
  >([]);
  const [selectedComm, setSelectedComm] =
    React.useState<ZoomCommunicationViewModel | null>(null);

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [selectedCalls, setSelectedCalls] = useState<number[]>([]);
  const [assignCallDialogOpen, setAssignCallDialogOpen] = React.useState(false);

  const [tableInteractions, setTableInteractions] =
    useState<TableInteractionsProps>({
      sortAsc: false,
      sortBy: "created",
      filterValue: null,
      page: 0,
      rowsPerPage: DEFAULT_ROWS_PER_PAGE,
    });
  const [totalCount, setTotalCount] = useState(0);
  const [bulkClicked, setBulkClicked] = useState(false);
  const dispatch = useDispatch();
  const zpReadyCountRef = React.useRef(0);

  const zoomPhoneNumber = user?.zoomPhoneNumber?.trim() || "";
  const zoomExtPhoneNumber = user?.zoomExtPhoneNumber?.trim() || "";
  const hasZoomAssignment = !!(zoomPhoneNumber || zoomExtPhoneNumber);

  useEffect(() => {
    loadZoomCommunications();
  }, [tableInteractions, zoomPhoneNumber, zoomExtPhoneNumber]);

  const handleCommunicationClick = (index: number) => {
    setSelectedCalls([]);
    setBulkClicked(false);
    const selected = zoomCommunications[index];
    setSelectedComm(selected);
    if (selected.matterId) {
      setMatterValue({
        id: selected.matterId,
        description: selected.matterName,
      });
      loadTasks(selected.matterId);
    } else {
      setMatterValue(null);
    }

    setTaskOptions([]);
    setTaskValue(null);
    setNotesValue(
      selected.type == EntryType.TEXT ? (selected.message ?? "") : ""
    );
    setAssignCallDialogOpen(true);
  };

  const handleBulkSave = () => {
    const comms = zoomCommunications.filter((c) =>
      selectedCalls.includes(c.id!)
    );
    setSelectedComm(comms[0]);
    const matter = comms.filter((c) => !!c.matterId)[0];

    if (matter?.matterId) {
      setMatterValue({
        id: matter.matterId,
        description: matter.matterName,
      });
      loadTasks(matter.matterId);
    } else {
      setMatterValue(null);
    }

    setTaskOptions([]);
    setTaskValue(null);

    const combinedMessages = comms
      .filter((c) => !!c.message)
      .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
      .map((c) => c.message)
      .join("\n \n");
    setNotesValue(combinedMessages);
    setAssignCallDialogOpen(true);
    setBulkClicked(true);
  };

  const loadZoomCommunications = async () => {
    if (!hasZoomAssignment) {
      setZoomCommunications([]);
      setTotalCount(0);
      return;
    }
    dispatch(startSpinner());
    const request = {
      phone: zoomPhoneNumber || "N/A",
      extPhone: zoomExtPhoneNumber || "N/A",
      limit: tableInteractions.rowsPerPage,
      offset: tableInteractions.rowsPerPage * tableInteractions.page,
      sortBy: tableInteractions.sortBy,
      sortAscending: tableInteractions.sortAsc,
      filterValue: tableInteractions.filterValue,
    } as ZoomTableRequest;

    try {
      const response = await ZoomService.postApiZoomPending(request);
      setZoomCommunications(response.communications ?? []);
      setTotalCount(response.totalCount ?? 0);
    } finally {
      dispatch(stopSpinner());
    }
  };

  async function loadTasks(matterId: number) {
    if (!matterId) {
      setTaskOptions([]);
      setTaskValue(null);
      setNotesValue("");
      return;
    }

    let responseOptions = await ClioService.getApiClioMattersTasks(matterId);
    responseOptions.push({ name: "Other", id: undefined });
    responseOptions = [
      { name: "Call with client", id: 0 },
      { name: "Call with OC", id: 1 },
      { name: "Text with client", id: 2 },
      { name: "Text with OC", id: 3 },
      { name: "Internal call", id: 4 },
      { name: "Internal text", id: 5 },
      ...responseOptions,
    ];
    setTaskOptions(responseOptions);
  }

  async function loadContacts(matterId: number) {
    if (!matterId) {
      setCallContactOptions([]);
      setCallContactValue(null);
      return;
    }

    const responseOptions = await ClioService.getApiClioMattersContacts(
      matterId
    );
    let contacts: PhoneContact[] = [];
    responseOptions.forEach((contact) => {
      const contactPhones = contact.phone_numbers?.map((m) => {
        return {
          name: `${contact.relationship_name}: ${contact.name}`,
          phoneNumber: normalizePhone(m.number),
        };
      });

      contacts = contacts.concat(contactPhones as PhoneContact[]);
    });

    setCallContactOptions(contacts);
  }

  function normalizePhone(phoneNumber: string | null | undefined) {
    if (!phoneNumber) {
      return "";
    }

    if (!phoneNumber.startsWith("+")) {
      phoneNumber = `+1${phoneNumber}`;
    }

    return phoneNumber
      .replace("-", "")
      .replace("-", "")
      .replace("(", "")
      .replace(")", "");
  }

  useEffect(() => {
    callContactValueRef.current = callContactValue;
  }, [callContactValue]);

  useEffect(() => {
    callMatterValueRef.current = callMatterValue;
  }, [callMatterValue]);

  useEffect(() => {
    window.onZoomPhoneIframeApiReady = () => {
      sendZoomInitConfig();
    };
    return () => {
      delete window.onZoomPhoneIframeApiReady;
    };
  }, []);

  const handleZoomEvents = React.useCallback(async (e: MessageEvent) => {
    if (e.origin !== ZOOM_ORIGIN) {
      return;
    }
    const data = e.data;
    if (!data?.type) {
      return;
    }

    switch (data.type) {
      case "zp-ready":
        sendZoomInitConfig();
        zpReadyCountRef.current += 1;
        // After sign-in, Zoom sends zp-ready again; reload iframe to show dial pad.
        if (zpReadyCountRef.current > 1) {
          zpReadyCountRef.current = 0;
          reloadZoomIframe();
        }
        break;
      case "zp-call-ringing-event": {
        setCallInProgress(true);
        break;
      }
      case "zp-call-connected-event": {
        if (callContactValueRef.current) {
          setTimeout(async () => {
            await ZoomService.postApiZoomMatter({
              callId: data.data.callId,
              matterId: callMatterValueRef.current?.id,
              matterName: callMatterValueRef.current?.description,
              clientName: callContactValueRef.current!.name,
            });
          }, 3000);
        }
        setCallInProgress(true);
        break;
      }
      case "zp-call-log-completed-event": {
        setTimeout(async () => {
          if (callContactValueRef.current) {
            await ZoomService.postApiZoomMatter({
              callId: data.data.callId,
              matterId: callMatterValueRef.current?.id,
              matterName: callMatterValueRef.current?.description,
              clientName: callContactValueRef.current!.name,
            });
          }
          setCallMatterValue(null);
          setCallContactOptions([]);
          setCallContactValue(null);
          setCallInProgress(false);

          await loadZoomCommunications();
        }, 3000);
        break;
      }
      case "zp-call-voicemail-received-event":
      case "zp-sms-log-event":
        setCallInProgress(false);
        setCallMatterValue(null);
        setCallContactOptions([]);
        setCallContactValue(null);
        setTimeout(async () => {
          await loadZoomCommunications();
        }, 3000);
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    window.addEventListener("message", handleZoomEvents);
    return () => {
      window.removeEventListener("message", handleZoomEvents);
    };
  }, [handleZoomEvents]);

  function handleCall() {
    const phoneNumber = callContactValue?.phoneNumber;
    const zoomUrl = `zoomphonecall://${phoneNumber}`;
    window.location.href = zoomUrl;
  }

  async function onSubmit() {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    toast.info("Creating Clio record!");

    if (bulkClicked && !!selectedCalls.length) {
      setBulkClicked(false);
      ZoomService.postApiZoomBulk({
        bulkCommIds: selectedCalls,
        callId: selectedComm?.callId,
        matterId: matterValue?.id,
        taskId: taskValue?.id,
        taskName: taskValue?.name,
        notes: notesValue,
      })
        .then(() => {
          const newComms = zoomCommunications.filter(
            (f) => !selectedCalls.includes(+f.id!)
          );
          setZoomCommunications(newComms);
          toast.success("Communications assigned.");
        })
        .catch(() => {
          toast.error("Could not assign communications.");
        })
        .finally(() => {
          setSelectedComm(null);
          setMatterValue(null);
          setTaskValue(null);
          setSelectedCalls([]);
          setNotesValue("");
          setTaskOptions([]);
          setIsSubmitting(false);
          setAssignCallDialogOpen(false);
        });
    } else {
      ZoomService.postApiZoom({
        commId: selectedComm?.id ?? undefined,
        callId: selectedComm?.callId,
        matterId: matterValue?.id,
        taskId: taskValue?.id,
        taskName: taskValue?.name,
        notes: notesValue,
      })
        .then(() => {
          const newComms = zoomCommunications.filter(
            (f) => f.callId !== selectedComm?.callId
          );
          setZoomCommunications(newComms);
          toast.success("Communication assigned.");
        })
        .catch(() => {
          toast.error("Could not assign communication.");
        })
        .finally(() => {
          setSelectedComm(null);
          setMatterValue(null);
          setTaskValue(null);
          setNotesValue("");
          setTaskOptions([]);
          setIsSubmitting(false);
          setAssignCallDialogOpen(false);
        });
    }
  }

  const handleSelectAllClick = () => {
    if (zoomCommunications.length === selectedCalls.length) {
      setSelectedCalls([]);
      return;
    }
    const newSelected = zoomCommunications.map((n) => n.id!);
    setSelectedCalls(newSelected);
  };

  const handleDeleteTableComm = async (id, e: any = null) => {
    if (e != null) {
      e.preventDefault();
      e.stopPropagation();
    }
    MySwal.fire({
      title: <strong>Confirm communication deletion</strong>,
      icon: "info",
      showCancelButton: true,
      cancelButtonText: "No, keep it",
      confirmButtonText: "Yes, remove",
      reverseButtons: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        await ZoomService.postApiZoomStatusDeleted(id);
        const newComms = zoomCommunications.filter((f) => f.id !== id);
        setZoomCommunications(newComms);
        toast.success("Communication removed.");
      }
    });
  };

  const handleBulkDelete = () => {
    selectedCalls.forEach((id) => handleDeleteTableComm(id));
  };

  const handleRowClick = (e, id: number) => {
    e.preventDefault();
    e.stopPropagation();
    const selectedIndex = selectedCalls.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = selectedCalls.concat(id);
    } else if (selectedIndex === 0) {
      newSelected = selectedCalls.slice(1);
    } else if (selectedIndex === selectedCalls.length - 1) {
      newSelected = selectedCalls.slice(0, -1);
    } else if (selectedIndex > 0) {
      newSelected = selectedCalls
        .slice(0, selectedIndex)
        .concat(selectedCalls.slice(selectedIndex + 1));
    }
    setSelectedCalls(newSelected);
  };

  const generateTableRow = (row: ZoomCommunicationViewModel, index) => {
    const isItemSelected = selectedCalls.indexOf(row.id!) !== -1;
    const labelId = `enhanced-table-checkbox-${index}`;
    return (
      <AppTableRow
        onClick={() => handleCommunicationClick(index)}
        role="checkbox"
        aria-checked={isItemSelected}
        tabIndex={-1}
        key={row.id}
        selected={isItemSelected}
        className={isItemSelected ? "table-row-accent" : ""}
        sx={{ cursor: "pointer" }}
      >
        <AppTableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            color="primary"
            checked={isItemSelected}
            onClick={(e) => handleRowClick(e, row.id!)}
            inputProps={{
              "aria-labelledby": labelId,
            }}
          />
        </AppTableCell>
        <RowNumberCell
          value={
            tableInteractions.page * tableInteractions.rowsPerPage + index + 1
          }
        />
        <AppTableCell align="left">
          {row.path} {row.type}
        </AppTableCell>
        <AppTableCell align="left">{row.matterName}</AppTableCell>
        <AppTableCell align="left">{row.clientPhone}</AppTableCell>
        <AppTableCell align="left">{row.clientName}</AppTableCell>
        <AppTableCell align="left" className="limit-rows">
          {row.message}
        </AppTableCell>
        <AppTableCell align="left">{row.duration}</AppTableCell>
        <AppTableCell align="left">
          {dayjs
            .utc(row.created)
            .local()
            .format("MM/DD/YYYY h:mmA")
            .toString()}
        </AppTableCell>
        <AppTableCell
          align="right"
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => handleDeleteTableComm(row.id, e)}
              aria-label="Delete communication"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </AppTableCell>
      </AppTableRow>
    );
  };

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    newSortBy: keyof ZoomCommunicationViewModel
  ) => {
    const toggledOrder =
      tableInteractions.sortBy !== newSortBy
        ? true
        : !tableInteractions.sortAsc;
    setTableInteractions({
      ...tableInteractions,
      sortAsc: toggledOrder,
      sortBy: newSortBy,
    });
  };

  const handleFilterChange = (newFilter: string) => {
    setTableInteractions({
      ...tableInteractions,
      filterValue: newFilter,
      page: 0,
    });
  };

  const handleChangePage = (newPage: number) => {
    setTableInteractions({ ...tableInteractions, page: newPage });
  };

  const handleChangeRowsPerPage = (rowsPerPage: number) => {
    setTableInteractions({
      ...tableInteractions,
      rowsPerPage,
      page: 0,
    });
  };

  return (
    <Box className="zoom-container" sx={{ width: "100%", pb: 3 }}>
      <PageHeader
        title="Communications"
        subtitle="Place calls, send texts, and review recent Zoom communications."
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(320px, 360px) minmax(0, 1fr)",
          },
          gap: 2.5,
          alignItems: "start",
        }}
      >
        <Stack spacing={2}>
          <FormSection
            title="Dial pad"
            subtitle="Sign in with Zoom if prompted — the dial pad appears after login."
          >
            <Stack spacing={2}>
              <ClioMatterAutocomplete
                disabled={callInProgress}
                value={callMatterValue}
                noOptionsText="No matters to call"
                onChange={(selectedValue) => {
                  setCallMatterValue(selectedValue);
                  loadContacts(selectedValue?.id as number);
                }}
              />

              <Autocomplete
                disablePortal
                disabled={!callMatterValue || callInProgress}
                options={callContactOptions}
                value={callContactValue}
                onChange={(
                  _event: any,
                  selectedValue: PhoneContact | null
                ) => {
                  setCallContactValue(selectedValue);
                }}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.name
                }
                renderInput={(params) => (
                  <TextField {...params} label="Choose a contact to call" />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Typography variant="body2" color="text.secondary">
                      {`${option.name} ${option.phoneNumber}`}
                    </Typography>
                  </li>
                )}
              />

              <AppButton
                disabled={!callContactValue || callInProgress}
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleCall}
              >
                {callContactValue?.phoneNumber
                  ? `Call ${callContactValue.phoneNumber}`
                  : "Call"}
              </AppButton>
            </Stack>
          </FormSection>

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderColor: "divider",
              boxShadow: "0 1px 2px rgba(27, 30, 51, 0.04)",
              display: "flex",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <iframe
              id="zoom-embeddable-phone-iframe"
              title="Zoom Phone"
              src={ZOOM_IFRAME_SRC}
              onLoad={() => sendZoomInitConfig()}
              allow="clipboard-read; clipboard-write https://applications.zoom.us"
            />
          </Paper>
        </Stack>

        <Stack spacing={2} sx={{ minWidth: 0 }}>
          {!hasZoomAssignment ? (
            <Paper sx={{ p: 3 }}>
              <Typography color="text.secondary">
                No Zoom account is assigned to your profile, so call logs cannot
                be loaded. Ask an admin to set your Zoom user on the user edit
                page.
              </Typography>
            </Paper>
          ) : (
            <>
          <FilterBar
            hideFilterButton
            hideClearButton
            leading={
              <TextField
                size="small"
                placeholder="Search in logs…"
                value={tableInteractions.filterValue ?? ""}
                onChange={(e) => handleFilterChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: "100%", sm: 260 },
                  bgcolor: "background.paper",
                  borderRadius: 1,
                }}
              />
            }
            trailing={
              <>
                <Tooltip title="Only texts could be sent together" arrow>
                  <span>
                    <AppButton
                      disabled={selectedCalls.length === 0}
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleBulkSave}
                    >
                      Save selected
                    </AppButton>
                  </span>
                </Tooltip>
                <AppButton
                  disabled={selectedCalls.length === 0}
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleBulkDelete}
                >
                  Delete selected
                </AppButton>
              </>
            }
          />

          <AppTable
            columns={mapLeadHeadCells(PHONE_TABLE_HEAD)}
            showRowNumber
            size="medium"
            leadingHeadCell={
              <LeadsSelectAllHeadCell
                numSelected={selectedCalls.length}
                rowCount={zoomCommunications.length}
                onSelectAllClick={handleSelectAllClick}
              />
            }
            trailingHeadCells={
              <AppTableHeadCell align="right" sx={{ width: 56 }}>
                Actions
              </AppTableHeadCell>
            }
            sortBy={tableInteractions.sortBy}
            sortAsc={tableInteractions.sortAsc}
            onRequestSort={(columnId) =>
              handleRequestSort(
                {} as React.MouseEvent<unknown>,
                columnId as keyof ZoomCommunicationViewModel
              )
            }
            extraColumnCount={2}
            isEmpty={zoomCommunications.length === 0}
            emptyTitle="No communications"
            pagination={{
              count: totalCount,
              page: tableInteractions.page,
              rowsPerPage: tableInteractions.rowsPerPage,
              rowsPerPageOptions: [10, 25, 50, 100],
              onPageChange: handleChangePage,
              onRowsPerPageChange: handleChangeRowsPerPage,
            }}
          >
            {zoomCommunications.map((row, index) =>
              generateTableRow(row, index)
            )}
          </AppTable>
            </>
          )}
        </Stack>
      </Box>

      <AppDialog
        open={assignCallDialogOpen}
        onClose={() => setAssignCallDialogOpen(false)}
        maxWidth="sm"
        title="Assign call"
        actions={
          <>
            <AppButton
              variant="outlined"
              color="inherit"
              onClick={() => setAssignCallDialogOpen(false)}
            >
              Cancel
            </AppButton>
            <AppButton
              disabled={!matterValue || !taskValue || isSubmitting}
              variant="contained"
              color="primary"
              onClick={onSubmit}
            >
              Save
            </AppButton>
          </>
        }
      >
        {selectedComm !== null && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <ClioMatterAutocomplete
              value={matterValue}
              noOptionsText="No matters"
              onChange={(selectedValue) => {
                setMatterValue(selectedValue);
                loadTasks(selectedValue?.id as number);
              }}
            />

            <Autocomplete
              disabled={!matterValue}
              options={taskOptions}
              value={taskValue}
              onChange={(
                _event: any,
                selectedValue: TaskPreviewResponse | null
              ) => {
                setTaskValue(selectedValue);
              }}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : (option.name as string)
              }
              renderInput={(params) => (
                <TextField {...params} label="Choose a task" fullWidth />
              )}
            />

            <TextField
              value={notesValue ?? ""}
              onChange={(e) => setNotesValue(e.target.value)}
              disabled={!matterValue}
              label="Notes"
              multiline
              rows={4}
              fullWidth
            />
          </Stack>
        )}
      </AppDialog>
    </Box>
  );
}
